// Radha — Statement of Account (Zoho Books / Zoho Invoice style). Pick a
// customer and get a chronological ledger of their invoices as debits, with paid
// invoices credited, a running balance, and a closing balance due. Built from the
// invoice reads only (no new tables); exportable to Excel and printable.
import React, { useMemo, useState } from "react";

import { useInvoices } from "../hooks/useBatchModules";
import { GroupExposurePanel } from "../components/GlobalDeskPanels";
import type { Invoice } from "../hooks/useBatchModules";
import { ExportButton } from "../components/TableSearch";
import { useMe, useRole } from "../lib/authSupabase";
import { upiPayLink } from "../lib/upi";
import { PayButton } from "../components/PayButton";
import { InstallmentPlan } from "../components/InstallmentPlan";
import { PortalMessages } from "../components/PortalMessages";
import { useCreatePortalLink } from "../hooks/useCustomerPortal";
import { writeErrorMessage } from "../hooks/useCollectionsWrites";
import { useCustomerPaymentProfiles, useInvoiceApStatuses, effectivePaymentChannel } from "../hooks/useApProcess";
import { ApStatusCell, ChannelSelect, EmailInvoiceCell } from "../components/ApStatusCell";
import { useInvoiceEmailAttempts } from "../hooks/useInvoiceEmail";
import { fmtMoney as inr, fmtMoneyFull as inrFull } from "../lib/money";


const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 500 };
const td: React.CSSProperties = { padding: "9px 8px" };

const isPaid = (s: string) => s === "paid";

interface Line { id: string; date: string; invoice: string; debit: number; credit: number; balance: number; status: string; issueDate: string; }

/** "2/10" style: pct off if paid within `days` of the invoice date. */
function earlyPayDiscount(issueDate: string, pct: number, days: number): number {
  if (!pct || !days) return 0;
  const cutoff = new Date(issueDate);
  cutoff.setDate(cutoff.getDate() + days);
  return new Date() <= cutoff ? pct : 0;
}

export function StatementsPanel() {
  const { data, isLoading, error } = useInvoices();
  const me = useMe(true);
  const { canWrite } = useRole();
  const createLink = useCreatePortalLink();
  const [shareNote, setShareNote] = useState<{ ok: boolean; text: string } | null>(null);
  const customers = useMemo(
    () => [...new Set((data ?? []).map((i) => i.customer_name))].sort(),
    [data],
  );
  const [customer, setCustomer] = useState<string>("");
  const active = customer || customers[0] || "";
  const { data: paymentProfiles } = useCustomerPaymentProfiles();
  const { data: apStatuses } = useInvoiceApStatuses();
  const activeProfile = useMemo(() => (paymentProfiles ?? []).find((p) => p.customer_name === active), [paymentProfiles, active]);
  const totalInvoicedActive = useMemo(
    () => (data ?? []).filter((i) => i.customer_name === active).reduce((s, i) => s + Number(i.total_amount), 0),
    [data, active],
  );
  const directPayMax = me?.direct_pay_max_threshold ?? Infinity;
  const poPortalMin = me?.po_portal_auto_threshold ?? Infinity;
  const activeChannel = effectivePaymentChannel(activeProfile, totalInvoicedActive, directPayMax, poPortalMin);
  const isPoPortal = activeChannel === "po_portal";
  const isInvoiceEmail = activeChannel === "invoice_email";
  const apStatusByInvoice = useMemo(() => new Map((apStatuses ?? []).map((s) => [s.invoice_id, s])), [apStatuses]);
  const { data: emailAttempts } = useInvoiceEmailAttempts();
  const emailAttemptsByInvoice = useMemo(() => {
    const m = new Map<string, typeof emailAttempts>();
    for (const a of emailAttempts ?? []) m.set(a.invoice_id, [...(m.get(a.invoice_id) ?? []), a]);
    return m;
  }, [emailAttempts]);

  const share = () => {
    setShareNote(null);
    createLink.mutate(active, {
      onSuccess: async (url) => {
        try { await navigator.clipboard.writeText(url); setShareNote({ ok: true, text: "Link copied — expires in 30 days" }); }
        catch { setShareNote({ ok: true, text: url }); }
      },
      onError: (e) => setShareNote({ ok: false, text: writeErrorMessage(e) }),
    });
  };

  const stmt = useMemo(() => {
    const rows = (data ?? [])
      .filter((i) => i.customer_name === active)
      .sort((a, b) => (a.issue_date < b.issue_date ? -1 : 1));
    let bal = 0;
    const lines: Line[] = rows.map((i: Invoice) => {
      const total = Number(i.total_amount) || 0;
      const paidAmt = Number(i.amount_paid) || 0;
      bal += total - paidAmt;
      return { id: i.id, date: i.issue_date, invoice: i.invoice_number, debit: total, credit: paidAmt, balance: bal, status: i.status, issueDate: i.issue_date };
    });
    const invoiced = lines.reduce((s, l) => s + l.debit, 0);
    const paid = lines.reduce((s, l) => s + l.credit, 0);
    const gstin = rows[0]?.customer_gstin ?? null;
    return { lines, invoiced, paid, due: invoiced - paid, gstin };
  }, [data, active]);

  if (isLoading) return <p style={{ padding: 24 }}>Loading…</p>;
  if (error) return <p style={{ padding: 24, color: "var(--status-critical)" }}>{(error as Error).message}</p>;
  if (customers.length === 0) return <p style={{ padding: 24, color: "var(--fg-muted)" }}>No invoices yet.</p>;

  return (
    <div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", margin: "4px 0 18px", flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, color: "var(--fg-muted)" }}>Customer</span>
        <select value={active} onChange={(e) => setCustomer(e.target.value)}
          style={{ padding: "6px 10px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, minWidth: 220 }}>
          {customers.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {stmt.gstin && <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>GSTIN {stmt.gstin}</span>}
        {canWrite && (
          <button onClick={share} disabled={createLink.isPending}
            title="Create an expiring, read-only link this customer can open without logging in."
            style={{ marginLeft: "auto", padding: "5px 12px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 7, cursor: "pointer", background: "none", color: "var(--fg-muted)" }}>
            {createLink.isPending ? "Creating…" : "Share portal link"}
          </button>
        )}
        <button onClick={() => window.print()}
          style={{ padding: "5px 12px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 7, cursor: "pointer", background: "none", color: "var(--fg-muted)", marginLeft: canWrite ? 0 : "auto" }}>
          Print
        </button>
        <ExportButton name={`Statement ${active}`} rows={() => stmt.lines.map((l) => ({
          Date: l.date, Invoice: l.invoice, Debit: l.debit, Credit: l.credit, Balance: l.balance, Status: l.status,
        }))} />
      </div>
      {shareNote && <p style={{ fontSize: 13, color: shareNote.ok ? "var(--status-good)" : "var(--status-critical)", margin: "-10px 0 14px", wordBreak: "break-all" }}>{shareNote.text}</p>}

      <div style={{ display: "flex", gap: 28, margin: "0 0 20px", flexWrap: "wrap" }}>
        <div><div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Invoiced</div><div style={{ font: "600 20px Inter" }}>{inr(stmt.invoiced)}</div></div>
        <div><div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Paid</div><div style={{ font: "600 20px Inter", color: "var(--status-good)" }}>{inr(stmt.paid)}</div></div>
        <div><div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Balance due</div><div style={{ font: "600 20px Inter", color: stmt.due > 0 ? "var(--status-critical)" : "var(--status-good)" }}>{inr(stmt.due)}</div></div>
        {stmt.due > 0 && isPoPortal && (
          <div style={{ alignSelf: "flex-end", fontSize: 12, color: "var(--fg-muted)", display: "flex", alignItems: "center", gap: 6 }}>
            Runs on this customer's PO / vendor-portal process — see status per invoice below.
            <ChannelSelect customerName={active} effectiveChannel={activeChannel} isExplicit={!!activeProfile} canWrite={canWrite} />
          </div>
        )}
        {stmt.due > 0 && isInvoiceEmail && (
          <div style={{ alignSelf: "flex-end", fontSize: 12, color: "var(--fg-muted)", display: "flex", alignItems: "center", gap: 6 }}>
            Invoiced by email — this customer pays on their own schedule (see per invoice below).
            <ChannelSelect customerName={active} effectiveChannel={activeChannel} isExplicit={!!activeProfile} canWrite={canWrite} />
          </div>
        )}
        {stmt.due > 0 && activeChannel === "direct_pay" && me?.tenant_upi_vpa && (
          <div style={{ alignSelf: "flex-end", display: "flex", alignItems: "center", gap: 8 }}>
            <a href={upiPayLink(me.tenant_upi_vpa, me.tenant_legal_name ?? "Radha", stmt.due, `${active} balance`)}
              style={{ display: "inline-block", padding: "6px 14px", background: "var(--status-good)", color: "#fff", borderRadius: 8, fontSize: 13, textDecoration: "none" }}>
              Pay via UPI
            </a>
            <ChannelSelect customerName={active} effectiveChannel={activeChannel} isExplicit={!!activeProfile} canWrite={canWrite} />
          </div>
        )}
      </div>

      <div className="sanctuary-scroll-x">
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 12 }}>
            <th style={th}>Date</th><th style={th}>Invoice</th>
            <th style={{ ...th, textAlign: "right" }}>Debit</th>
            <th style={{ ...th, textAlign: "right" }}>Credit</th>
            <th style={{ ...th, textAlign: "right" }}>Balance</th>
            <th style={th}>Status</th>
            <th style={th}>Pay</th>
          </tr>
        </thead>
        <tbody>
          {stmt.lines.map((l, idx) => {
            const lineDue = l.debit - l.credit;
            const discPct = earlyPayDiscount(l.issueDate, me?.early_pay_discount_pct ?? 0, me?.early_pay_discount_days ?? 0);
            const discountedDue = discPct > 0 ? Math.round((lineDue * (1 - discPct / 100)) * 100) / 100 : lineDue;
            return (
              <React.Fragment key={l.invoice + idx}>
                <tr style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={td}>{l.date}</td>
                  <td style={td}>{l.invoice}</td>
                  <td style={{ ...td, textAlign: "right" }}>{inrFull(l.debit)}</td>
                  <td style={{ ...td, textAlign: "right", color: l.credit ? "var(--status-good)" : "var(--fg-muted)" }}>{l.credit ? inrFull(l.credit) : "—"}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{inrFull(l.balance)}</td>
                  <td style={{ ...td, textTransform: "capitalize", color: isPaid(l.status) ? "var(--status-good)" : "var(--fg-muted)" }}>{l.status.replace(/_/g, " ")}</td>
                  <td style={td}>
                    {lineDue <= 0 ? <span style={{ color: "var(--fg-muted)" }}>—</span> : isPoPortal ? (
                      <ApStatusCell invoiceId={l.id} apStatus={apStatusByInvoice.get(l.id)} canWrite={canWrite} />
                    ) : isInvoiceEmail ? (
                      <EmailInvoiceCell invoiceId={l.id} attempts={emailAttemptsByInvoice.get(l.id) ?? []} canWrite={canWrite} />
                    ) : (
                      <span style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
                        <PayButton invoiceId={l.id} outstanding={lineDue} amount={discPct > 0 ? discountedDue : undefined}
                          tenantName={me?.tenant_legal_name ?? "Radha"} label={discPct > 0 ? `Pay ${inrFull(discountedDue)}` : "Pay"} />
                        {discPct > 0 && <span style={{ fontSize: 10.5, color: "var(--status-good)" }}>{discPct}% early-payment discount applied</span>}
                      </span>
                    )}
                  </td>
                </tr>
                {lineDue > 0 && (
                  <tr>
                    <td colSpan={7} style={{ padding: "0 8px 10px" }}>
                      <InstallmentPlan invoiceId={l.id} outstanding={lineDue} tenantName={me?.tenant_legal_name ?? "Radha"} canWrite={canWrite} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          <tr style={{ borderTop: "2px solid var(--border-strong)" }}>
            <td style={{ ...td, fontWeight: 600 }} colSpan={4}>Closing balance due</td>
            <td style={{ ...td, textAlign: "right", fontWeight: 700, color: stmt.due > 0 ? "var(--status-critical)" : "var(--status-good)" }}>{inrFull(stmt.due)}</td>
            <td style={td}></td>
            <td style={td}></td>
          </tr>
        </tbody>
      </table>
      </div>

      <GroupExposurePanel />
      <PortalMessages canWrite={canWrite} />
    </div>
  );
}
